import Phaser from 'phaser';
import { COLORS, PLAYER_SPEED, PLAYER_JUMP_VEL, PLAYER_DODGE_SPEED, PLAYER_DODGE_DURATION, PLAYER_SHIELD_DURATION, PLAYER_SHIELD_COOLDOWN, PLAYER_DASH_SPEED, PLAYER_DASH_DURATION, PLAYER_DASH_COOLDOWN, PLAYER_ATTACK_COOLDOWN, PLAYER_HEAVY_ATTACK_COOLDOWN, PLAYER_SKILL_COOLDOWN, PlayerStats, createPlayerStats } from '../utils/constants';
import { HeadTracker } from '../systems/HeadTracker';
import { soundManager } from '../systems/SoundManager';
import { TouchControls, TouchState } from './TouchControls';
import { weaponSystem } from '../systems/WeaponSystem';

export class Player {
  scene: Phaser.Scene;
  sprite: Phaser.Physics.Arcade.Sprite;
  stats: PlayerStats;
  hp = 100;
  maxHp = 100;
  shieldActive = false;
  invulnerable = false;

  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private keyJ!: Phaser.Input.Keyboard.Key;
  private keyK!: Phaser.Input.Keyboard.Key;
  private keyL!: Phaser.Input.Keyboard.Key;
  private keySpace!: Phaser.Input.Keyboard.Key;
  private keyShift!: Phaser.Input.Keyboard.Key;
  private keyR!: Phaser.Input.Keyboard.Key;
  private keyA!: Phaser.Input.Keyboard.Key;
  private keyD!: Phaser.Input.Keyboard.Key;
  private keyW!: Phaser.Input.Keyboard.Key;
  private keyS!: Phaser.Input.Keyboard.Key;

  private lastAttackTime = 0;
  private lastHeavyTime = 0;
  private lastSkillTime = 0;
  private lastShieldTime = 0;
  private lastDashTime = 0;
  private isDodging = false;
  private isDashing = false;
  private canAttack = true;
  private facingRight = true;
  private shieldGraphic: Phaser.GameObjects.Graphics | null = null;
  private headTracker: HeadTracker | null = null;
  private keyboardOnly = false;
  private touchControls: TouchControls | null = null;
  private attackHitbox: Phaser.GameObjects.Rectangle | null = null;
  private attackActive = false;
  private currentAttackType: 'normal' | 'heavy' | 'skill' | null = null;
  private trailGraphics: Phaser.GameObjects.Graphics;
  private runParticles: Phaser.GameObjects.Graphics;

  constructor(scene: Phaser.Scene, x: number, y: number, headTracker: HeadTracker | null) {
    this.scene = scene;
    this.headTracker = headTracker;
    this.stats = createPlayerStats();
    this.createTexture();

    this.sprite = scene.physics.add.sprite(x, y, 'player');
    this.sprite.setCollideWorldBounds(true);
    this.sprite.setGravityY(0);
    (this.sprite.body as Phaser.Physics.Arcade.Body).setSize(28, 60);
    (this.sprite.body as Phaser.Physics.Arcade.Body).setOffset(10, 8);
    this.sprite.setDepth(10);

    this.trailGraphics = scene.add.graphics().setDepth(9);
    this.runParticles = scene.add.graphics().setDepth(9);

    this.cursors = scene.input.keyboard!.createCursorKeys();
    this.keyJ = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.J);
    this.keyK = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.K);
    this.keyL = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.L);
    this.keySpace = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SPACE);
    this.keyShift = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.SHIFT);
    this.keyR = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.R);
    this.keyA = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.A);
    this.keyD = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.D);
    this.keyW = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.W);
    this.keyS = scene.input.keyboard!.addKey(Phaser.Input.Keyboard.KeyCodes.S);

    this.touchControls = new TouchControls(scene);
  }

  setKeyboardOnly(val: boolean) {
    this.keyboardOnly = val;
  }

  getTouchState(): TouchState | null {
    return this.touchControls?.isEnabled() ? this.touchControls.state : null;
  }

  private createTexture() {
    const canvas = document.createElement('canvas');
    canvas.width = 48;
    canvas.height = 72;
    const ctx = canvas.getContext('2d')!;
    const cx = 24, cy = 36;

    ctx.shadowColor = '#00f0ff';
    ctx.shadowBlur = 8;

    ctx.fillStyle = '#222244';
    ctx.fillRect(14, 52, 8, 18);
    ctx.fillRect(26, 52, 8, 18);
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(16, 56, 4, 4);
    ctx.fillRect(28, 56, 4, 4);

    ctx.shadowBlur = 4;
    ctx.fillStyle = '#1a2a4a';
    ctx.fillRect(10, 18, 28, 34);
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(14, 22, 20, 4);
    ctx.fillStyle = '#0a3a5a';
    ctx.fillRect(12, 28, 24, 18);

    ctx.shadowBlur = 12;
    ctx.fillStyle = '#00f0ff';
    ctx.beginPath();
    ctx.arc(cx, 34, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.arc(cx, 34, 2, 0, Math.PI * 2);
    ctx.fill();

    ctx.shadowBlur = 4;
    ctx.fillStyle = '#1a2a4a';
    ctx.fillRect(2, 20, 8, 22);
    ctx.fillRect(38, 20, 8, 22);
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(4, 30, 4, 4);
    ctx.fillRect(40, 30, 4, 4);

    ctx.shadowBlur = 6;
    ctx.fillStyle = '#1a2a4a';
    ctx.fillRect(12, 0, 24, 20);
    ctx.shadowBlur = 10;
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(14, 4, 20, 6);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(16, 5, 4, 4);
    ctx.fillRect(28, 5, 4, 4);
    ctx.fillStyle = '#9945ff';
    ctx.fillRect(22, 0, 4, 4);

    ctx.fillStyle = '#9945ff';
    ctx.shadowBlur = 6;
    ctx.fillRect(6, 16, 6, 6);
    ctx.fillRect(36, 16, 6, 6);

    ctx.fillStyle = '#9945ff';
    ctx.fillRect(10, 48, 28, 4);
    ctx.fillStyle = '#00f0ff';
    ctx.fillRect(22, 48, 4, 4);

    ctx.fillStyle = '#1a2a4a';
    ctx.fillRect(12, 64, 12, 6);
    ctx.fillRect(24, 64, 12, 6);
    ctx.fillStyle = '#9945ff';
    ctx.fillRect(14, 66, 8, 2);
    ctx.fillRect(26, 66, 8, 2);

    ctx.shadowBlur = 0;
    if (!this.scene.textures.exists('player')) {
      const tex = this.scene.textures.createCanvas('player', 48, 72);
      tex.context.drawImage(canvas, 0, 0);
      tex.refresh();
    }
  }

  update(time: number, delta: number) {
    if (this.hp <= 0) return;

    this.handleMovement(time);
    this.handleAttacks(time);
    this.handleShield(time);
    this.handleDash(time);
    this.updateTrail(time);
  }

  private getHeadActions() {
    if (this.headTracker && !this.keyboardOnly) {
      return this.headTracker.getAction();
    }
    return null;
  }

  private handleMovement(time: number) {
    if (this.isDodging || this.isDashing) return;

    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    const head = this.getHeadActions();
    const touch = this.getTouchState();

    let moveX = 0;
    let jump = false;

    if (head) {
      if (head.leanLeft || head.leanRight) {
        const pose = this.headTracker?.getPose();
        if (pose) {
          moveX = Phaser.Math.Clamp(pose.yaw / 20, -1, 1);
        } else {
          moveX = head.leanLeft ? -1 : 1;
        }
      }
      if (head.lookUp) jump = true;
    }

    if (touch) {
      if (Math.abs(touch.moveX) > 0.15) moveX = touch.moveX;
      if (touch.moveY < -0.5) jump = true;
    }

    if (this.keyA.isDown || this.cursors.left.isDown) moveX = -1;
    if (this.keyD.isDown || this.cursors.right.isDown) moveX = 1;
    if (Phaser.Input.Keyboard.JustDown(this.keyW) || Phaser.Input.Keyboard.JustDown(this.cursors.up)) jump = true;

    body.setVelocityX(moveX * PLAYER_SPEED);

    if (moveX < 0) {
      this.facingRight = false;
      this.sprite.setFlipX(true);
    } else if (moveX > 0) {
      this.facingRight = true;
      this.sprite.setFlipX(false);
    }

    if (jump && body.blocked.down) {
      body.setVelocityY(PLAYER_JUMP_VEL);
    }

    if (head && head.nod && !this.isDodging && !this.isDashing) {
      this.startDodge(time);
    }
    if (touch && touch.moveY > 0.5 && !this.isDodging && !this.isDashing && body.blocked.down) {
      this.startDodge(time);
    }
    if ((this.keyS.isDown || this.cursors.down.isDown) && !this.isDodging && !this.isDashing && body.blocked.down) {
      this.startDodge(time);
    }

    if (head && (head.leanLeft || head.leanRight)) {
      this.stats.headMovements++;
    }
  }

  private startDodge(time: number) {
    if (this.isDodging) return;
    this.isDodging = true;
    soundManager.play('dodge');
    this.stats.dodges++;
    this.invulnerable = true;

    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    const dir = this.facingRight ? 1 : -1;
    body.setVelocityX(dir * PLAYER_DODGE_SPEED);
    this.sprite.setAlpha(0.4);

    this.scene.time.delayedCall(PLAYER_DODGE_DURATION, () => {
      this.isDodging = false;
      this.invulnerable = false;
      this.sprite.setAlpha(1);
      body.setVelocityX(0);
    });
  }

  private handleAttacks(time: number) {
    if (this.isDodging || this.isDashing) return;

    const touch = this.getTouchState();

    if ((Phaser.Input.Keyboard.JustDown(this.keyJ) || (touch && touch.attack)) && time - this.lastAttackTime > PLAYER_ATTACK_COOLDOWN) {
      this.lastAttackTime = time;
      this.performAttack('normal');
    }

    if ((Phaser.Input.Keyboard.JustDown(this.keyK) || (touch && touch.heavy)) && time - this.lastHeavyTime > PLAYER_HEAVY_ATTACK_COOLDOWN) {
      this.lastHeavyTime = time;
      this.performAttack('heavy');
      this.stats.heavyAttacks++;
    }

    if ((Phaser.Input.Keyboard.JustDown(this.keyL) || (touch && touch.skill)) && time - this.lastSkillTime > PLAYER_SKILL_COOLDOWN) {
      this.lastSkillTime = time;
      this.performAttack('skill');
      this.stats.skillUses++;
    }
  }

  private performAttack(type: 'normal' | 'heavy' | 'skill') {
    this.currentAttackType = type;
    const weapon = weaponSystem.getEquipped();
    soundManager.play(type === 'skill' ? 'skill' : type === 'heavy' ? 'heavy_attack' : 'attack');
    const dir = this.facingRight ? 1 : -1;
    const offsetX = dir * 30;
    const offsetY = -5;

    const flashColor = type === 'skill' ? COLORS.purple : type === 'heavy' ? COLORS.yellow : (weapon.color || COLORS.cyan);
    const range = type === 'skill' ? 60 : type === 'heavy' ? 50 : 40;
    const size = type === 'skill' ? 40 : type === 'heavy' ? 36 : 28;

    const flash = this.scene.add.graphics().setDepth(15);
    flash.fillStyle(flashColor, 0.5);
    flash.fillCircle(this.sprite.x + offsetX, this.sprite.y + offsetY, size);
    flash.fillStyle(flashColor, 0.2);
    flash.fillCircle(this.sprite.x + offsetX, this.sprite.y + offsetY, size + 10);

    this.scene.time.delayedCall(type === 'heavy' ? 200 : 100, () => {
      flash.destroy();
    });

    this.attackHitbox = this.scene.add.rectangle(
      this.sprite.x + offsetX,
      this.sprite.y + offsetY,
      size * 2,
      size * 2,
      flashColor,
      0
    ).setDepth(15);
    this.attackActive = true;

    const attackDuration = type === 'heavy' ? 250 : type === 'skill' ? 300 : 150;
    this.scene.time.delayedCall(attackDuration, () => {
      this.attackActive = false;
      this.currentAttackType = null;
      if (this.attackHitbox) {
        this.attackHitbox.destroy();
        this.attackHitbox = null;
      }
    });
  }

  fireAttack(type: 'normal' | 'heavy' | 'skill' | 'shield' | 'dash') {
    const time = this.scene.time.now;
    if (this.isDodging || this.isDashing || this.hp <= 0) return;
    switch (type) {
      case 'normal':
        if (time - this.lastAttackTime > PLAYER_ATTACK_COOLDOWN) {
          this.lastAttackTime = time;
          this.performAttack('normal');
        }
        break;
      case 'heavy':
        if (time - this.lastHeavyTime > PLAYER_HEAVY_ATTACK_COOLDOWN) {
          this.lastHeavyTime = time;
          this.performAttack('heavy');
          this.stats.heavyAttacks++;
        }
        break;
      case 'skill':
        if (time - this.lastSkillTime > PLAYER_SKILL_COOLDOWN) {
          this.lastSkillTime = time;
          this.performAttack('skill');
          this.stats.skillUses++;
        }
        break;
      case 'shield':
        if (!this.shieldActive && time - this.lastShieldTime > PLAYER_SHIELD_COOLDOWN) {
          this.activateShield(time);
        }
        break;
      case 'dash':
        if (!this.isDashing && time - this.lastDashTime > PLAYER_DASH_COOLDOWN) {
          soundManager.play('dash');
          this.isDashing = true;
          this.invulnerable = true;
          this.lastDashTime = time;
          const body = this.sprite.body as Phaser.Physics.Arcade.Body;
          const dir = this.facingRight ? 1 : -1;
          body.setVelocityX(dir * PLAYER_DASH_SPEED);
          this.sprite.setAlpha(0.6);
          const trail = this.scene.add.graphics().setDepth(9);
          trail.fillStyle(COLORS.cyan, 0.3);
          trail.fillRect(this.sprite.x - 18, this.sprite.y - 30, 36, 60);
          this.scene.time.delayedCall(PLAYER_DASH_DURATION, () => {
            this.isDashing = false;
            this.invulnerable = false;
            this.sprite.setAlpha(1);
            body.setVelocityX(0);
            trail.destroy();
          });
        }
        break;
    }
  }

  getAttackHitbox(): Phaser.GameObjects.Rectangle | null {
    if (this.attackActive && this.attackHitbox) {
      return this.attackHitbox;
    }
    return null;
  }

  getAttackType(): 'normal' | 'heavy' | 'skill' | null {
    return this.currentAttackType;
  }

  private handleShield(time: number) {
    const touch = this.getTouchState();
    if ((this.keySpace.isDown || (touch && touch.shield)) && !this.shieldActive && time - this.lastShieldTime > PLAYER_SHIELD_COOLDOWN) {
      this.activateShield(time);
    }
  }

  private activateShield(time: number) {
    this.shieldActive = true;
    this.lastShieldTime = time;
    soundManager.play('shield');
    this.stats.shieldUses++;

    this.shieldGraphic = this.scene.add.graphics().setDepth(12);
    this.shieldGraphic.lineStyle(3, COLORS.cyan, 0.8);
    this.shieldGraphic.strokeCircle(0, 0, 36);
    this.shieldGraphic.lineStyle(1, COLORS.cyan, 0.4);
    this.shieldGraphic.strokeCircle(0, 0, 42);
    this.shieldGraphic.setPosition(this.sprite.x, this.sprite.y);

    this.scene.time.delayedCall(PLAYER_SHIELD_DURATION, () => {
      this.shieldActive = false;
      if (this.shieldGraphic) {
        this.shieldGraphic.destroy();
        this.shieldGraphic = null;
      }
    });
  }

  private handleDash(time: number) {
    const touch = this.getTouchState();
    if ((Phaser.Input.Keyboard.JustDown(this.keyShift) || (touch && touch.dash)) && !this.isDashing && !this.isDodging && time - this.lastDashTime > PLAYER_DASH_COOLDOWN) {
      soundManager.play('dash');
      this.isDashing = true;
      this.invulnerable = true;
      this.lastDashTime = time;

      const body = this.sprite.body as Phaser.Physics.Arcade.Body;
      const dir = this.facingRight ? 1 : -1;
      body.setVelocityX(dir * PLAYER_DASH_SPEED);

      this.sprite.setAlpha(0.6);

      const trail = this.scene.add.graphics().setDepth(9);
      trail.fillStyle(COLORS.cyan, 0.3);
      trail.fillRect(this.sprite.x - 18, this.sprite.y - 30, 36, 60);

      this.scene.time.delayedCall(PLAYER_DASH_DURATION, () => {
        this.isDashing = false;
        this.invulnerable = false;
        this.sprite.setAlpha(1);
        body.setVelocityX(0);
        trail.destroy();
      });
    }
  }

  private updateTrail(time: number) {
    this.trailGraphics.clear();
    this.runParticles.clear();

    if (this.isDashing || this.isDodging) {
      this.trailGraphics.fillStyle(COLORS.cyan, 0.15);
      this.trailGraphics.fillRect(this.sprite.x - 18, this.sprite.y - 30, 36, 60);
    }

    if (this.shieldGraphic) {
      this.shieldGraphic.setPosition(this.sprite.x, this.sprite.y);
      const pulse = 0.5 + Math.sin(time * 0.006) * 0.3;
      this.shieldGraphic.setAlpha(pulse);
    }

    // Running particles
    const body = this.sprite.body as Phaser.Physics.Arcade.Body;
    if (Math.abs(body.velocity.x) > 50 && body.blocked.down) {
      this.runParticles.fillStyle(COLORS.cyan, 0.1);
      this.runParticles.fillCircle(this.sprite.x, this.sprite.y + 30, 3);
    }
  }

  takeDamage(amount: number): boolean {
    if (this.invulnerable || this.shieldActive || this.hp <= 0) return false;

    this.hp = Math.max(0, this.hp - amount);
    soundManager.play('hit_taken');
    this.stats.damagesTaken++;
    this.stats.combo = 0;

    this.sprite.setTint(0xff0000);
    this.scene.time.delayedCall(150, () => {
      if (this.sprite.active) this.sprite.clearTint();
    });

    this.invulnerable = true;
    this.scene.time.delayedCall(500, () => {
      this.invulnerable = false;
    });

    return true;
  }

  heal(amount: number) {
    this.hp = Math.min(this.maxHp, this.hp + amount);
  }

  incrementCombo() {
    this.stats.combo++;
    if (this.stats.combo > this.stats.maxCombo) {
      this.stats.maxCombo = this.stats.combo;
    }
    if (this.stats.combo > 1) {
      soundManager.play('combo');
    }
  }

  getCooldownProgress(type: 'attack' | 'heavy' | 'skill' | 'shield' | 'dash'): number {
    const now = this.scene.time.now;
    switch (type) {
      case 'attack':
        return Math.min(1, (now - this.lastAttackTime) / PLAYER_ATTACK_COOLDOWN);
      case 'heavy':
        return Math.min(1, (now - this.lastHeavyTime) / PLAYER_HEAVY_ATTACK_COOLDOWN);
      case 'skill':
        return Math.min(1, (now - this.lastSkillTime) / PLAYER_SKILL_COOLDOWN);
      case 'shield':
        return Math.min(1, (now - this.lastShieldTime) / PLAYER_SHIELD_COOLDOWN);
      case 'dash':
        return Math.min(1, (now - this.lastDashTime) / PLAYER_DASH_COOLDOWN);
      default:
        return 1;
    }
  }

  isAlive(): boolean {
    return this.hp > 0;
  }

  destroy() {
    this.sprite.destroy();
    this.trailGraphics.destroy();
    this.runParticles.destroy();
    if (this.shieldGraphic) this.shieldGraphic.destroy();
    if (this.attackHitbox) this.attackHitbox.destroy();
    if (this.touchControls) this.touchControls.destroy();
  }
}
